/**
 * Phase 107 (Plan 107-10 / D-16) — apply-preset confirmation, plus the
 * preset coordinate set itself.
 *
 * Mirrors `RevokeConfirmModal.tsx`'s title/body/footer structure via the
 * shared `Modal` shell, with **no** re-typed confirmation-word input — same
 * precedent every other confirm modal in this directory already set. Per
 * D-16, applying a preset is Reset's useful inverse: start from Mon-Fri
 * 08:00-18:00 rather than painting fifty cells by hand. The body states the
 * actual effect: the window is painted with the armed band, and every hour
 * outside it is cleared.
 */
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';

/**
 * The 50 Mon-Fri 08:00-18:00 coordinates, built with a nested loop rather
 * than a literal list. This is exactly the shape production project 817
 * (Ahlsell Denmark SLA) actually painted — snapshotted at
 * `.planning/phases/104-retire-the-coverage-grid-team-model/104-PROD-SNAPSHOT-817.json`
 * (dayOfWeek 0 through 4, hour 8 through 17) — which is why it is the
 * sanctioned default. The grid itself still spans the full 24x7 (D-07): this
 * preset is a starting point, not a constraint, since the out-of-window
 * nights and weekends are where the premium rates sit.
 */
export const PRESET_MON_FRI_0800_1800: { dayOfWeek: number; hour: number }[] = (() => {
  const coords: { dayOfWeek: number; hour: number }[] = [];
  for (let dayOfWeek = 0; dayOfWeek <= 4; dayOfWeek++) {
    for (let hour = 8; hour <= 17; hour++) {
      coords.push({ dayOfWeek, hour });
    }
  }
  return coords;
})();

export interface ApplyPresetConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  armedBandName: string;
  isLoading?: boolean;
}

export function ApplyPresetConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  armedBandName,
  isLoading = false,
}: ApplyPresetConfirmModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Apply preset?"
      ariaLabelledBy="apply-preset-modal-title"
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={onConfirm} isLoading={isLoading}>
            Apply preset
          </Button>
        </div>
      }
    >
      <p className="text-gray-600">
        {`This paints Mon–Fri 08:00–18:00 with "${armedBandName}" and clears every hour outside that window.`}
      </p>
    </Modal>
  );
}

export default ApplyPresetConfirmModal;
